// ============================================================================
// NODE SIZE CONSTANTS
// ============================================================================

export const NODE_SIZES = {
    cluster: {
        collapsed: { width: 200, height: 120 },
        expanded: { minWidth: 500, minHeight: 350 }
    },
    namespace: {
        collapsed: { width: 160, height: 90 },
        expanded: { minWidth: 350, minHeight: 250 }
    },
    workload: { width: 140, height: 80 },
    service: { width: 140, height: 80 },
    node: { width: 150, height: 90 },
    external: { width: 130, height: 80 },
    externalIp: { width: 140, height: 70 },
    knownIp: { width: 160, height: 80 },
    container: { minWidth: 300, minHeight: 200 },

    // AWS Cloud Resource node sizes
    awsAccount: {
        collapsed: { width: 220, height: 120 },
        expanded: { minWidth: 500, minHeight: 350 }
    },
    awsVpc: {
        collapsed: { width: 180, height: 100 },
        expanded: { minWidth: 400, minHeight: 280 }
    },
    awsGlobalGroup: {
        collapsed: { width: 180, height: 100 },
        expanded: { minWidth: 400, minHeight: 280 }
    },
    awsSubnet: {
        expanded: { minWidth: 300, minHeight: 180 }
    },
    awsResource: { width: 150, height: 85 },

    // OCI Cloud Resource node sizes
    ociTenancy: {
        collapsed: { width: 220, height: 120 },
        expanded: { minWidth: 500, minHeight: 350 }
    },
    ociVcn: {
        collapsed: { width: 180, height: 100 },
        expanded: { minWidth: 400, minHeight: 280 }
    },
    ociGlobalGroup: {
        collapsed: { width: 180, height: 100 },
        expanded: { minWidth: 400, minHeight: 280 }
    },
    ociSubnet: {
        expanded: { minWidth: 300, minHeight: 180 }
    },
    ociResource: { width: 150, height: 85 }
} as const;

// ============================================================================
// LAYOUT CONSTANTS
// ============================================================================

export const LAYOUT = {
    /** Padding inside containers (increased for flow edge visibility) */
    containerPadding: 50,

    /** Header height for container labels */
    headerHeight: 35,

    /** Spacing between sibling nodes */
    nodeSpacing: 100,

    /** Spacing between namespace nodes (larger for flow visibility) */
    namespaceSpacing: 100,

    /** Spacing between top-level clusters */
    clusterSpacing: 80,

    /** Maximum items per row in grid layout */
    maxItemsPerRow: 6,

    /** Minimum items per row */
    minItemsPerRow: 2,

    /** Margin around canvas for external nodes */
    externalNodeMargin: 200,

    /** Minimum spacing between external nodes */
    externalNodeSpacing: 50
} as const;

// ============================================================================
// ANIMATION CONSTANTS
// ============================================================================

export const ANIMATION = {
    /** Duration of expansion/collapse animation in ms */
    expandDuration: 300,

    /** Easing function for animations */
    easing: 'ease-in-out',

    /** Delay before removing animation class */
    cleanupDelay: 350
} as const;

// ============================================================================
// CACHE CONSTANTS
// ============================================================================

export const CACHE = {
    /** Time-to-live for cached data in ms (5 minutes) */
    ttl: 5 * 60 * 1000,

    /** Maximum number of clusters to cache */
    maxClusters: 20,

    /** Maximum number of namespaces to cache */
    maxNamespaces: 100
} as const;

// ============================================================================
// FLOW CONSTANTS
// ============================================================================

export const FLOWS = {
    /** Maximum flows to fetch at cluster level */
    maxClusterFlows: 5000,

    /** Maximum flows to fetch at namespace level */
    maxNamespaceFlows: 2000,

    /** Maximum L7 logs to fetch at cluster level */
    maxClusterL7Logs: 3000,

    /** Maximum L7 logs to fetch at namespace level */
    maxNamespaceL7Logs: 1000,

    /** Maximum edges to display (for performance) */
    maxEdges: 100,

    /** Threshold for aggregating external IPs */
    externalIpAggregationThreshold: 10
} as const;

// ============================================================================
// COLOR CONSTANTS (for flow edges)
// ============================================================================

export const COLORS = {
    flowAllowed: '#10b981',      // Green
    flowDenied: '#ef4444',       // Red
    flowSuspicious: '#f59e0b',   // Yellow/Amber

    clusterBorder: '#6366f1',    // Indigo
    namespaceBorder: '#3b82f6',  // Blue
    workloadBorder: '#8b5cf6',   // Purple
    serviceBorder: '#06b6d4',    // Cyan
    externalBorder: '#f97316',   // Orange
    knownIpBorder: '#a855f7',    // Purple (distinct from external)

    // AWS Cloud Resource colors
    awsAccountBorder: '#ff9900', // AWS Orange
    awsVpcBorder: '#527fff',     // AWS Blue
    awsSubnetBorder: '#22c55e',  // Green for subnets
    awsGlobalBorder: '#7d8998',  // AWS Gray
    awsResourceBorder: '#ff9900', // AWS Orange

    // OCI Cloud Resource colors
    ociTenancyBorder: '#C74634', // Oracle Red
    ociVcnBorder: '#527fff',     // Blue (same as AWS VPC for consistency)
    ociSubnetBorder: '#22c55e',  // Green for subnets
    ociGlobalBorder: '#7d8998',  // Gray (same as AWS Global)
    ociResourceBorder: '#C74634', // Oracle Red

    containerBackground: 'rgba(0, 0, 0, 0.02)',
    containerBackgroundDark: 'rgba(255, 255, 255, 0.02)'
} as const;
